import { Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ONBOARDING_PALETTE } from '../onboarding-palette';
import type { OnboardingSnapshot } from '../onboarding-snapshot';
import type { OnboardingStep } from '../onboarding-steps';
import { BrandPanelCard } from './BrandPanelCard';
import { CollabsPanelCard } from './CollabsPanelCard';
import { FreePlanStrip } from './FreePlanStrip';
import { CompanionPreview } from './CompanionPreview';
import { SalesPanelCard } from './SalesPanelCard';
import { OnboardingTaglineBadge } from './OnboardingTaglineBadge';
import { OnboardingPanelFrame } from './OnboardingPanelFrame';
import { panelCardState, panelPillKey, panelShowsCompanion } from './panel-state';

/** Flat background of the panel and colour of its pill, per step (mock-up). */
const PANEL_THEME: Record<OnboardingStep, { bg: string; accent: string }> = {
  brand: { bg: 'lilac.300', accent: 'navDark.aiText' },
  creators: { bg: 'primary.900', accent: 'rose.300' },
  collabs: { bg: 'green.300', accent: 'green.300' },
  sales: { bg: 'discoveryAccent', accent: 'discoveryAccent' },
  connect: { bg: 'discoveryAccent', accent: 'discoveryAccent' },
  welcome: { bg: 'lilac.300', accent: 'navDark.aiText' },
};

/**
 * Right side of the onboarding: the brand space building itself as the brand
 * answers. Hidden below `lg`, where the form goes full width.
 */
export const OnboardingBrandPanel = ({
  step,
  snapshot,
}: {
  step: OnboardingStep;
  snapshot: OnboardingSnapshot;
}) => {
  const { t } = useTranslation();
  const theme = PANEL_THEME[step];

  return (
    <OnboardingPanelFrame bg={theme.bg}>
      <OnboardingTaglineBadge color={theme.accent} borderWidth={step === 'creators' ? '1px' : 0}>
        {t(panelPillKey(step))}
      </OnboardingTaglineBadge>

      <Flex flex="1" align="center">
        {panelShowsCompanion(step) ? (
          <CompanionPreview step={step} preferences={snapshot.creatorPreferences} />
        ) : (
          <Flex
            direction="column"
            gap="10px"
            w="full"
            p="12px"
            borderRadius="32px"
            bg={ONBOARDING_PALETTE.cardsTray}
            borderWidth="1px"
            borderColor={ONBOARDING_PALETTE.cardsTrayBorder}
          >
            <BrandPanelCard snapshot={snapshot} state={panelCardState('brand', step)} />
            <CollabsPanelCard snapshot={snapshot} state={panelCardState('collabs', step)} />
            <SalesPanelCard snapshot={snapshot} step={step} state={panelCardState('sales', step)} />
          </Flex>
        )}
      </Flex>

      {snapshot.isFreePlan && <FreePlanStrip salesChannel={snapshot.salesChannel} />}
    </OnboardingPanelFrame>
  );
};
